import {useEffect, useId, useRef, type KeyboardEvent} from 'react';
import {createPortal} from 'react-dom';
import {PhoneOff} from 'lucide-react';
import {useIntl} from 'react-intl';
import type {EndedCall} from '../model/types';
import {formatCallDuration} from '../services/format';
import {callSurfaceMessages as m} from '../messages';

interface GroupEndedStageProps {
    readonly ended: EndedCall;
    readonly onClose: () => void;
}

// D-CONF-02/S08: the full-screen group call ends in place, so the person sees why it closed.
export const GroupEndedStage = ({ended, onClose}: GroupEndedStageProps) => {
    const intl = useIntl();
    const titleId = useId();
    const bodyId = useId();
    const close = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        close.current?.focus();
    }, []);

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape' || event.key === 'Tab') {
            event.preventDefault();
        }
        if (event.key === 'Escape') {
            onClose();
        }
    };

    const body = ended.durationMs > 0
        ? intl.formatMessage(m.everyoneLeftDuration, {duration: formatCallDuration(ended.durationMs)})
        : intl.formatMessage(m.everyoneLeft);

    return createPortal(
        <section
            aria-label={intl.formatMessage(m.groupEndedRegion, {name: ended.identity.name ?? ''})}
            data-testid="group-call-ended-stage"
            className="phq-call-ui fixed bottom-0 left-14 right-0 top-0 z-[55] bg-[var(--phq-call-canvas)] [background-image:var(--phq-call-stage-gradient)]"
        >
            <div className="absolute inset-0 flex items-center justify-center bg-[rgba(9,9,11,.82)] p-4" onKeyDown={onKeyDown}>
                <div
                    role="alertdialog"
                    aria-modal="true"
                    aria-labelledby={titleId}
                    aria-describedby={bodyId}
                    className="phq-call-enter flex w-full max-w-[420px] flex-col items-center gap-3 rounded-[20px] bg-[var(--phq-call-surface)] px-6 pb-6 pt-7 text-center shadow-[inset_0_0_0_1px_var(--phq-call-line)]"
                >
                    <span aria-hidden="true" className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--phq-call-surface-muted)] text-[var(--phq-call-ink-muted)]">
                        <PhoneOff size={24}/>
                    </span>
                    <h2 id={titleId} className="text-lg font-semibold leading-tight">{intl.formatMessage(m.callEnded)}</h2>
                    <p id={bodyId} className="text-sm tabular-nums text-[var(--phq-call-ink-muted)]">{body}</p>
                    <div className="mt-2 flex gap-2">
                        <button
                            ref={close}
                            type="button"
                            data-testid="group-call-ended-close"
                            onClick={onClose}
                            className="phq-call-press inline-flex h-10 items-center justify-center gap-2 rounded-[10px] bg-[var(--phq-call-surface-muted)] px-3.5 text-sm font-semibold"
                        >
                            {intl.formatMessage(m.close)}
                        </button>
                    </div>
                </div>
            </div>
        </section>,
        document.body,
    );
};
